import { useCallback, useEffect, useState } from "react"

const IOS_DISMISS_KEY = "creatix_ios_install_dismissed"
const INSTALL_DISMISS_KEY = "creatix_install_dismissed"
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

function isIOSDevice(): boolean {
  if (typeof window === "undefined") return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & { MSStream?: unknown }).MSStream
}

function isDismissedRecently(key: string): boolean {
  const raw = localStorage.getItem(key)
  if (!raw) return false
  const ts = Number(raw)
  return Date.now() - ts < DISMISS_TTL_MS
}

export interface InstallPromptResult {
  canInstall: boolean
  isInstalled: boolean
  isIOS: boolean
  promptInstall: () => Promise<boolean>
  dismissIOS: () => void
  dismissInstall: () => void
  isIOSDismissed: boolean
  isInstallDismissed: boolean
}

export function useInstallPrompt(): InstallPromptResult {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(() => {
    if (typeof window === "undefined") return false
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    )
  })
  const [isIOSDismissed, setIsIOSDismissed] = useState(() => isDismissedRecently(IOS_DISMISS_KEY))
  const [isInstallDismissed, setIsInstallDismissed] = useState(() =>
    isDismissedRecently(INSTALL_DISMISS_KEY)
  )
  const isIOS = isIOSDevice()

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstall)
    window.addEventListener("appinstalled", onInstalled)
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall)
      window.removeEventListener("appinstalled", onInstalled)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    setDeferredPrompt(null)
    if (outcome === "accepted") setIsInstalled(true)
    return outcome === "accepted"
  }, [deferredPrompt])

  const dismissIOS = useCallback(() => {
    localStorage.setItem(IOS_DISMISS_KEY, String(Date.now()))
    setIsIOSDismissed(true)
  }, [])

  const dismissInstall = useCallback(() => {
    localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now()))
    setIsInstallDismissed(true)
  }, [])

  return {
    canInstall: !!deferredPrompt && !isInstalled && !isInstallDismissed,
    isInstalled,
    isIOS,
    promptInstall,
    dismissIOS,
    dismissInstall,
    isIOSDismissed,
    isInstallDismissed,
  }
}

export function useMeaningfulActionTrigger(): boolean {
  const [triggered, setTriggered] = useState(false)

  useEffect(() => {
    const onMeaningful = () => setTriggered(true)
    window.addEventListener("meaningful-action", onMeaningful)
    return () => window.removeEventListener("meaningful-action", onMeaningful)
  }, [])

  return triggered
}

export function dispatchMeaningfulAction(detail: string) {
  window.dispatchEvent(new CustomEvent("meaningful-action", { detail }))
}
